import { getToolboxBridge } from "@/lib/toolbox/bridge";
import { TOOLBOX_HOME_PATH } from "@/lib/toolbox/tools";

/**
 * Where to send someone after they sign in (PI-SPEC-SW-1407).
 *
 * `AppLayout` remembers the page a signed-out visitor asked for before it
 * redirects to `/login`; the sign-in paths (Microsoft SSO callback, local
 * password form) take it back. sessionStorage survives the round trip to
 * login.microsoftonline.com in the same tab, like the PKCE verifier does.
 */
export const RETURN_TO_STORAGE_KEY = "spectree.auth.returnTo";

const MAX_RETURN_PATH_LENGTH = 2048;

/** Pages that must never be a sign-in destination. */
const EXCLUDED_PATHS = new Set(["/", "/login", "/register"]);

/**
 * A same-origin path worth returning to: starts with exactly one `/`, carries
 * no backslash (browsers read `/\host` as `//host`), and is not the root or a
 * sign-in page.
 */
export function isSafeReturnPath(path: unknown): path is string {
  if (typeof path !== "string" || path.length === 0 || path.length > MAX_RETURN_PATH_LENGTH) return false;
  if (!path.startsWith("/") || path.startsWith("//") || path.includes("\\")) return false;
  const pathname = path.split(/[?#]/, 1)[0] ?? "";
  if (EXCLUDED_PATHS.has(pathname) || pathname.startsWith("/auth/")) return false;
  return true;
}

/** Remember the page to return to after sign-in; unsafe paths are ignored. */
export function rememberReturnTo(path: unknown): void {
  if (!isSafeReturnPath(path)) return;
  try {
    sessionStorage.setItem(RETURN_TO_STORAGE_KEY, path);
  } catch {
    // Storage unavailable: the visitor lands on the default page instead.
  }
}

/** The page to land on when there is nothing to return to. */
export function defaultLandingPath(): string {
  return getToolboxBridge() ? TOOLBOX_HOME_PATH : "/dashboard";
}

/** The remembered page (cleared once read), else the default landing page. */
export function takeReturnTo(): string {
  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(RETURN_TO_STORAGE_KEY);
    sessionStorage.removeItem(RETURN_TO_STORAGE_KEY);
  } catch {
    stored = null;
  }
  return isSafeReturnPath(stored) ? stored : defaultLandingPath();
}
